import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { TEMPLATES, miniFor } from "../lib/templates";
import { sanitize } from "../lib/sanitize";
import {
  listCreatives, getCreative, createCreative, updateCreative, renameCreative,
  duplicateCreative, deleteCreative, uploadAsset, previewUrl, isAuthed,
  listTemplates, getTemplateManifest, deleteOwnTemplate, API,
} from "../lib/api";
import Preview from "../components/Preview";
import SdkStage from "../components/SdkStage";
import TvRoom from "../components/TvRoom";
import InspectorForm from "../components/InspectorForm";
import ExportDialog from "../components/ExportDialog";
import { isBls, mintStudyId, validateBls, serverErrors } from "../lib/bls";
import UploadTemplateDialog from "../components/UploadTemplateDialog";
import "../studio.css";

// Non-Stage SDK templates (overlays like Poll) render transparently over a linear background.
// They get the SAME standard Background block (Image / Video / Partner VAST) as classic templates.
const DEFAULT_SDK_BG = {
  type: "video", image: "",
  videos: [{ url: "https://storage.googleapis.com/interactive-media-ads/media/android.mp4", kind: "mp4", bitrate: 1500, w: 1280, h: 720 }],
};
function ensureSdkBackground(cfg, manifest) {
  if (!cfg) return cfg;
  const usesStage = !!(cfg.useStage || (manifest && manifest.defaults && manifest.defaults.useStage));
  if (usesStage || cfg.background) return cfg;
  return { ...cfg, background: JSON.parse(JSON.stringify(DEFAULT_SDK_BG)), duration: cfg.duration == null ? 15 : cfg.duration };
}

// Standard picker = the 3 promoted SDK templates + classic QR. Classic carousel/poll
// stay in lib/templates.js so existing saved creatives keep rendering.
const STANDARD_SDK_IDS = ["poll-interactive", "quiz-interactive", "carousel-overlay", "split-5050", "whatsapp-cta", "lband-sdk", "bls-vast", "bls-web"];
const STANDARD_CLASSIC_IDS = ["qr"];

export default function Studio() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "standard";
  const setTab = (t) => setSp(t === "standard" ? {} : { tab: t }, { replace: true });
  const [tid, setTid] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [name, setName] = useState("");
  const [i, setI] = useState(0);
  const [voted, setVoted] = useState(false);
  const [lib, setLib] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [present, setPresent] = useState(null);
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("classic");
  const [manifest, setManifest] = useState(null);
  const [tpls, setTpls] = useState([]);
  const [tplsLoaded, setTplsLoaded] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [squeeze, setSqueeze] = useState(null); // null | baking | ready | error
  const [fieldErrors, setFieldErrors] = useState({}); // BLS inline validation (client pre-check + server 400)
  useEffect(() => { setFieldErrors({}); }, [cfg]);
  const tvRef = useRef(null);

  useEffect(() => { if (!isAuthed()) nav("/login"); }, [nav]);
  const reloadTpls = useCallback(() => { listTemplates().then(setTpls).catch(() => {}).finally(() => setTplsLoaded(true)); }, []);
  useEffect(() => { reloadTpls(); }, [reloadTpls]);

  const reloadLib = useCallback(() => { listCreatives(search).then(setLib).catch(() => {}); }, [search]);
  useEffect(() => { reloadLib(); }, [reloadLib]);

  // L-band: poll the background squeezeback bake after save / while editing
  useEffect(() => {
    if (!editingId || tid !== "lband-sdk") return undefined;
    let stop = false, timer;
    const tick = async () => {
      try {
        const c = await getCreative(editingId);
        if (stop) return;
        setSqueeze(c.squeezeStatus || null);
        const sc = c.config || {};
        if (c.squeezeStatus === "ready" && sc.squeezedAssetPath) {
          setCfg((prev) => (prev && !prev.squeezedAssetPath
            ? { ...prev, squeezedAssetPath: sc.squeezedAssetPath, squeezedFrom: sc.squeezedFrom, squeezedLayout: sc.squeezedLayout }
            : prev));
        }
        if (c.squeezeStatus === "baking") timer = setTimeout(tick, 3000);
      } catch { /* non-blocking */ }
    };
    tick();
    return () => { stop = true; clearTimeout(timer); };
  }, [editingId, tid, currentVersion]);

  // boot with a default standard template (first promoted SDK default, else classic QR)
  useEffect(() => {
    if (cfg || !tplsLoaded) return;
    const first = STANDARD_SDK_IDS.map((sid) => tpls.find((t) => t.id === sid)).find(Boolean);
    if (first) { openSdkTemplate(first); return; }
    setTid("qr"); setCfg(TEMPLATES.qr.defaults());
    // eslint-disable-next-line
  }, [tplsLoaded, tpls]);

  const openStandard = (id) => {
    setKind("classic"); setManifest(null);
    setTid(id); setCfg(TEMPLATES[id].defaults()); setEditingId(null);
    setCurrentVersion(1); setName(""); setI(0); setVoted(false);
  };
  const openSdkTemplate = async (item) => {
    const m = await getTemplateManifest(item.id);
    setKind("sdk"); setManifest(m); setTid(item.id);
    const d = JSON.parse(JSON.stringify(m.defaults || {}));
    if (d.studyId === "__MINT__") d.studyId = mintStudyId();
    setCfg(ensureSdkBackground(d, m)); setEditingId(null); setFieldErrors({});
    setCurrentVersion(1); setName(""); setI(0); setVoted(false);
  };
  const openSaved = async (id) => {
    const c = await getCreative(id);
    if ((c.kind || "classic") === "sdk") {
      const m = await getTemplateManifest(c.template).catch(() => null);
      if (!m) toast.error("This template is no longer available — its config can still be exported.");
      setKind("sdk"); setManifest(m);
      setTid(c.template); setCfg(ensureSdkBackground(c.config, m)); setEditingId(c.id);
    } else {
      setKind("classic"); setManifest(null);
      setTid(c.template); setCfg(c.config); setEditingId(c.id);
    }
    setCurrentVersion(c.currentVersion); setName(c.name); setI(0); setVoted(false);
    setSqueeze(c.squeezeStatus || null);
    setTab("mine");
  };
  const dup = async (id) => { const r = await duplicateCreative(id); toast.success("Duplicated"); reloadLib(); openSaved(r.id); };
  const del = async (id) => {
    if (!window.confirm("Delete this template permanently? This can't be undone.")) return;
    await deleteCreative(id); toast.success("Deleted"); reloadLib();
    if (editingId === id) openStandard("qr");
  };
  const rename = async (rec) => {
    const nm = window.prompt("Rename template:", rec.name);
    if (!nm || nm.trim() === rec.name) return;
    await renameCreative(rec.id, nm.trim());
    if (editingId === rec.id) setName(nm.trim());
    toast.success("Renamed"); reloadLib();
  };

  // BLS: block Save/Publish on invalid config (client pre-check, then server is the authority)
  const blsGate = () => {
    if (!isBls(tid)) return true;
    const e = validateBls(cfg);
    if (Object.keys(e).length) { setFieldErrors(e); toast.error(Object.values(e)[0]); return false; }
    return true;
  };
  const blsCall = async (fn) => {
    try { return await fn(); } catch (err) {
      const { message, errors } = serverErrors(err);
      setFieldErrors(errors); toast.error(Object.values(errors)[0] || message); return null;
    }
  };
  const blsWarn = (r) => (r.warnings || []).forEach((w) => toast.warning(w, { duration: 9000 }));
  const save = async () => {
    if (!cfg) { toast("Open a template first"); return; }
    if (!blsGate()) return;
    if (/"blob:/.test(JSON.stringify(cfg))) {
      toast.warning("An image or video upload hasn't finished (or failed) — saving now will break it on reload. Wait a moment or click Retry, then save again.", { duration: 8000 });
    }
    if (editingId) {
      const r = await blsCall(() => updateCreative(editingId, cfg));
      if (!r) return;
      setCurrentVersion(r.version);
      if (tid === "lband-sdk") setSqueeze("baking");
      toast.success(`Saved · new version v${r.version}`); blsWarn(r);
      reloadLib();
      return;
    }
    const nm = window.prompt("Name this template:", `${(kind === "sdk" ? (manifest?.name || tid) : TEMPLATES[tid].name)} — ${cfg.advertiser || cfg.advertiserName || "custom"}`);
    if (!nm) return;
    const r = await blsCall(() => createCreative({ name: nm, template: tid, config: cfg }));
    if (!r) return;
    setEditingId(r.id); setCurrentVersion(r.version); setName(nm); setTab("mine");
    if (tid === "lband-sdk") setSqueeze("baking");
    toast.success("Saved to My Templates"); blsWarn(r); reloadLib();
  };

  const onUpload = async (file) => await uploadAsset(file, editingId, tid);

  const handleKey = (e) => {
    if (!cfg) return; let handled = true;
    if (tid === "carousel") {
      const n = cfg.frames.length || 1;
      if (e.key === "ArrowRight") setI((x) => (x + 1) % n);
      else if (e.key === "ArrowLeft") setI((x) => (x - 1 + n) % n);
      else if (e.key === "Enter" || e.key === " ") toast(`→ clickThru: ${(cfg.frames[i] || {}).url || ""}`);
      else handled = false;
    } else if (tid === "poll") {
      const m = cfg.options.length || 1;
      if (voted) { if (e.key === "Enter") setVoted(false); else handled = false; }
      else if (e.key === "ArrowRight") setI((x) => (x + 1) % m);
      else if (e.key === "ArrowLeft") setI((x) => (x - 1 + m) % m);
      else if (e.key === "Enter" || e.key === " ") { setVoted(true); toast(`vote: ${(cfg.options[i] || {}).label || ""}`); }
      else handled = false;
    } else handled = false;
    if (handled) e.preventDefault();
  };

  const doPresent = () => {
    if (!editingId) { toast("Save the creative first to present the real runtime"); return; }
    setPresent(editingId);
  };
  const doExport = async () => {
    if (!blsGate()) return;
    if (!editingId) {
      // auto-save so the exported tag is live
      const nm = window.prompt("Name this template to export:", `${(kind === "sdk" ? (manifest?.name || tid) : TEMPLATES[tid].name)} — ${cfg.advertiser || cfg.advertiserName || "custom"}`);
      if (!nm) return;
      const r = await blsCall(() => createCreative({ name: nm, template: tid, config: cfg }));
      if (!r) return;
      blsWarn(r);
      setEditingId(r.id); setCurrentVersion(r.version); setName(nm); setTab("mine"); reloadLib();
      setExportOpen(true);
      return;
    }
    setExportOpen(true);
  };

  const exportWarnings = () => {
    const w = [];
    if (isBls(tid)) return w;
    if (cfg && /data:/.test(JSON.stringify(cfg))) w.push("An asset is an <b>uploaded data-URI</b> — replace with a hosted URL before trafficking.");
    if (cfg && /"blob:/.test(JSON.stringify(cfg))) w.push("An image or video is only <b>staged locally</b> — click its Save button to upload it before trafficking.");
    const bg = cfg?.background || {};
    if (bg.type === "partner") {
      w.push(bg.partnerVast ? "Partner VAST is fetched <b>live</b> on each serve; their video + tracking are merged into this tag." : "Partner VAST selected but no tag URL entered.");
      return w;
    }
    const hasMp4 = (bg.type === "video" ? (bg.videos || []) : []).some((v) => v.url && v.kind !== "hls");
    if (!hasMp4) w.push("No progressive <b>MP4</b> rendition — a default sample MP4 is included so non-HLS players still render.");
    return w;
  };

  if (!cfg || !tid) return <div className="cts-scope" />;
  const t = kind === "sdk" ? (manifest || { name: tid, content: [], layouts: [] }) : TEMPLATES[tid];

  return (
    <div className="app">
        <header className="bar">
          <h1 className="page-title" data-testid="page-title">Studio</h1>
          <div className="spacer" />
          <button className="btn ghost" onClick={doPresent} data-testid="present-btn">▶ Present</button>
          <button className="btn" onClick={save} data-testid="save-btn">Save</button>
          <button className="btn primary" onClick={doExport} data-testid="export-btn">{tid === "bls-web" ? "Get survey URL" : "Export VAST"}</button>
        </header>

        <div className="body">
          <nav className="rail" aria-label="Templates" data-testid="rail">
            {tab === "standard" ? (
              <>
                <div className="eyebrow">Standard · v1</div>
                {STANDARD_SDK_IDS.map((sid) => tpls.find((t) => t.id === sid)).filter(Boolean).map((it) => (
                  <button key={it.id} className="tcard" aria-pressed={editingId === null && tid === it.id}
                    onClick={() => openSdkTemplate(it)} data-testid={`std-card-${it.id}`}>
                    <div className="thumb">
                      {it.thumbnail ? <img src={it.thumbnail} alt="" /> : <span className="sdk-badge-thumb">SDK</span>}
                    </div>
                    <div className="row"><span className="tname">{it.name}</span><span className="tier">Standard</span></div>
                    <div className="tdesc" title={it.desc}>{it.desc}</div>
                  </button>
                ))}
                {STANDARD_CLASSIC_IDS.map((id) => (
                  <button key={id} className="tcard" aria-pressed={editingId === null && tid === id}
                    onClick={() => openStandard(id)} data-testid={`std-card-${id}`}>
                    <div className="thumb" dangerouslySetInnerHTML={{ __html: sanitize(miniFor(id)) }} />
                    <div className="row"><span className="tname">{TEMPLATES[id].name}</span><span className="tier">{TEMPLATES[id].tier}</span></div>
                    <div className="tdesc" title={TEMPLATES[id].desc}>{TEMPLATES[id].desc}</div>
                  </button>
                ))}
                <div className="hint" style={{ marginTop: 4 }}>Customize a standard template, then Save it into My Templates.</div>

                <div className="eyebrow" style={{ marginTop: 16 }}>SDK templates</div>
                {tpls.filter((it) => !STANDARD_SDK_IDS.includes(it.id)).map((it) => (
                  <button key={it.id} className="tcard" aria-pressed={editingId === null && tid === it.id}
                    onClick={() => openSdkTemplate(it)} data-testid={`sdk-card-${it.id}`}>
                    <div className="thumb">
                      {it.thumbnail ? <img src={it.thumbnail} alt="" /> : <span className="sdk-badge-thumb">SDK</span>}
                    </div>
                    <div className="row"><span className="tname">{it.name}</span><span className="tier">{it.builtin ? "Standard" : "Uploaded"}</span></div>
                    <div className="tdesc" title={it.desc}>{it.desc}</div>
                    {it.owned && !it.builtin && (
                      <span className="tcard-del" role="button" tabIndex={0} title="Delete this template"
                        data-testid={`tpl-owner-delete-${it.id}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`Delete "${it.name}"? Creatives built on it will stop working.`)) return;
                          await deleteOwnTemplate(it.id); toast.success("Template deleted"); reloadTpls();
                        }}>×</span>
                    )}
                  </button>
                ))}
                <button className="addbtn" type="button" onClick={() => setUploadOpen(true)} data-testid="upload-template-btn"
                  style={{ marginTop: 4 }}>+ Upload a template</button>
                <div className="hint" style={{ marginTop: 4 }}>
                  Build against the Creative SDK and upload — it renders in the sandboxed preview.{" "}
                  <a href={`${API}/sdk/docs`} target="_blank" rel="noreferrer" data-testid="sdk-docs-link"
                    style={{ color: "var(--accent-strong)", fontWeight: 600 }}>Read the SDK docs (v1.0)</a>
                </div>
              </>
            ) : (
              <>
                <div className="eyebrow">My Templates</div>
                <div className="rail-search">
                  <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name…" data-testid="library-search" />
                </div>
                {lib.length === 0 && <div className="empty">{search ? `No templates match “${search}”.` : "No saved templates yet. Open a Standard template, customize it, and hit Save."}</div>}
                {lib.map((rec) => (
                  <div key={rec.id} className="tcard" aria-pressed={editingId === rec.id} data-testid={`saved-card-${rec.id}`}
                    style={{ cursor: "pointer" }} onClick={() => openSaved(rec.id)}>
                    <div className="thumb">
                      {rec.thumbnail ? <img src={rec.thumbnail} alt="" /> : <span dangerouslySetInnerHTML={{ __html: sanitize(miniFor(rec.template)) }} />}
                    </div>
                    <div className="row">
                      <span className="tname">{rec.name}</span>
                      <span className="tier verpill">v{rec.currentVersion}</span>
                    </div>
                    <div className="tdesc">{(TEMPLATES[rec.template]?.name) || (rec.kind === "sdk" ? "SDK template" : rec.template)} · <span className="mono">{rec.id}</span></div>
                    {rec.createdByEmail && <div className="tby">by {rec.createdByEmail}</div>}
                    <div className="card-acts">
                      <button data-testid={`open-${rec.id}`} onClick={(e) => { e.stopPropagation(); openSaved(rec.id); }}>Edit</button>
                      <button data-testid={`rename-${rec.id}`} onClick={(e) => { e.stopPropagation(); rename(rec); }}>Rename</button>
                      <button data-testid={`dup-${rec.id}`} onClick={(e) => { e.stopPropagation(); dup(rec.id); }}>Duplicate</button>
                      <button className="danger" data-testid={`del-${rec.id}`} onClick={(e) => { e.stopPropagation(); del(rec.id); }}>Delete</button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </nav>

          <main className="stage">
            <div className="screenwrap">
              <div className="screen-top">
                <div className="eyebrow" data-testid="stage-label">
                  {editingId ? `Editing saved · ${t.name} · v${currentVersion}` : `Draft · ${t.name}`}
                </div>
                {tid === "lband-sdk" && editingId && squeeze && (
                  <div className="eyebrow" data-testid="squeeze-status"
                    style={{ color: squeeze === "ready" ? "var(--good, #16a34a)" : squeeze === "error" ? "var(--bad, #dc2626)" : "var(--accent, #3b82f6)" }}>
                    {squeeze === "baking" ? "Baking squeezeback video…" : squeeze === "ready" ? "Squeezeback video baked" : "Squeezeback bake failed"}
                  </div>
                )}
                <div className="live"><span className="dot" /> rendering in-browser</div>
              </div>
              {kind === "sdk" ? (
                <>
                  <div className="tv" data-testid="sdk-screen" style={t.dims ? { aspectRatio: `${t.dims.w} / ${t.dims.h}` } : undefined}>
                    <SdkStage key={tid} templateId={tid} config={cfg} creativeId={editingId} onTrack={() => {}}
                      onDuration={(sec) => {
                        const r = Math.round(sec);
                        if (cfg.background?.type === "video" && r > 0 && r !== cfg.duration) {
                          setCfg((prev) => ({ ...prev, duration: r }));
                        }
                      }} />
                  </div>
                  <div className="playbar" data-testid="sdk-remote">
                    {[["ArrowLeft", "←"], ["ArrowRight", "→"], ["Enter", "↵ Select"], ["ArrowUp", "↑ Pause"]].map(([k, lbl]) => (
                      <button key={k} className="btn sm" data-testid={`sdk-key-${k}`}
                        onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: k }))}>{lbl}</button>
                    ))}
                  </div>
                </>
              ) : (
                <Preview cfg={cfg} tid={tid} i={i} voted={voted} onKeyDown={handleKey} tvRef={tvRef}
                  onDuration={(sec) => {
                    const r = Math.round(sec);
                    if (cfg.background?.type === "video" && r > 0 && r !== cfg.duration) {
                      setCfg((prev) => ({ ...prev, duration: r }));
                    }
                  }} />
              )}
              <div className="remote">
                <span><span className="keycap">←</span> <span className="keycap">→</span> browse</span>
                <span><span className="keycap">↵</span> select</span>
                <span><span className="keycap">↑</span> pause</span>
                <span style={{ color: "var(--text-faint)" }}>click screen to focus</span>
              </div>
            </div>
          </main>

          <aside className="inspector">
            <div className="insp-head">
              <h2>Customize <span className="badge">{editingId ? "Saved" : "Draft"}</span></h2>
              <p data-testid="insp-sub">{name || t.name}</p>
            </div>
            <InspectorForm tid={tid} schema={kind === "sdk" ? manifest : null} cfg={cfg} setCfg={setCfg} onUpload={onUpload} resetIndex={() => setI(0)} errors={fieldErrors} />
          </aside>
        </div>

      <UploadTemplateDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={() => reloadTpls()} />

      <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)}
        creativeId={editingId} currentVersion={currentVersion} warnings={exportWarnings()} tid={tid} />

      {present && (
        <div className="present-overlay" data-testid="present-modal">
          <TvRoom creativeId={present} share onExit={() => setPresent(null)} />
        </div>
      )}
    </div>
  );
}
